"use client";

import { Suspense, useEffect, useRef, useState } from "react";
import { useOspLogin } from "@/src/features/auth/compat/useOspLogin";
import type { Web3LoginSupportedType } from "@/src/features/auth/compat/sdk-types";
import { useSnapshot } from "valtio";
import { LottieLoading } from "@ospweb/sdk-ui";
import { deviceDetector } from "utils";
import { LoginStore, clearJustLoggedOutFlag } from "@store";
import { EmailLoginModal } from "@/src/components/EmailLogin";
import { SignInIntroPhase } from "@/src/components/SignInIntro";
import { getBridge, MessageType } from "@/src/lib/bridge";
import { useOnboardingHarnessQuery } from "@/src/hooks/onboarding/useOnboardingHarnessQuery";

type LoginPagePhase = "intro" | "login";

function resolveInitialLoginPhase(
  harness: ReturnType<typeof useOnboardingHarnessQuery>,
): LoginPagePhase {
  if (harness.enabled && harness.loginPhase === "login") {
    return "login";
  }
  return "intro";
}

/** 独立登录页：Phase A Intro → Phase B EmailLoginFlow（D12） */
function LoginPageContent() {
  const harness = useOnboardingHarnessQuery();
  const { login } = useOspLogin();
  const gateReadySentRef = useRef(false);
  const [phase, setPhase] = useState<LoginPagePhase>(() =>
    resolveInitialLoginPhase(harness),
  );

  useEffect(() => {
    const hasLoginHistory = localStorage.getItem("edw.last_login_type");
    if (hasLoginHistory && phase === "intro") {
      setPhase("login");
    }
  }, []);
  const {
    isLoading: isBusinessLoading,
    isSocialLoginIntent,
    isFetching,
    isLoggedIn,
  } = useSnapshot(LoginStore);

  const loginController = async (type: Web3LoginSupportedType) => {
    LoginStore.isSocialLoginIntent = true;
    try {
      await login(type);
    } finally {
      LoginStore.isSocialLoginIntent = false;
    }
  };

  useEffect(() => {
    if (!harness.enabled) {
      return;
    }
    if (harness.loginPhase === "login") {
      setPhase("login");
    } else if (harness.loginPhase === "intro") {
      setPhase("intro");
    }
  }, [harness.enabled, harness.loginPhase]);

  const activePhase: LoginPagePhase =
    harness.enabled && harness.loginPhase != null ? harness.loginPhase : phase;

  useEffect(() => {
    if (isFetching || isLoggedIn) {
      return;
    }
    clearJustLoggedOutFlag();
  }, [isFetching, isLoggedIn]);

  useEffect(() => {
    if (!deviceDetector.isApp()) {
      return;
    }
    if (isFetching || isLoggedIn) {
      return;
    }
    if (gateReadySentRef.current) {
      return;
    }
    gateReadySentRef.current = true;
    getBridge().send(MessageType.WEB_LOGIN_GATE_READY, {
      source: "login_page",
      loginState: "needs_login",
    });
  }, [isFetching, isLoggedIn]);

  if (isLoggedIn) {
    return (
      <div className="flex h-dvh w-full items-center justify-center">
        <LottieLoading style={{ width: 80, height: 80 }} />
      </div>
    );
  }

  if (activePhase === "intro") {
    return (
      <SignInIntroPhase
        initialSlideIndex={harness.introSlide ?? 0}
        freezeCarousel={harness.enabled && harness.introSlide !== null}
        onGetStarted={() => setPhase("login")}
      />
    );
  }

  return (
    <EmailLoginModal
      open
      mode="page"
      onClose={() => {}}
      onSocialClick={loginController}
      loading={isBusinessLoading || !!isSocialLoginIntent}
      hideCloseButton
    />
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginPageContent />
    </Suspense>
  );
}
